import { useTranslations } from "@zapier/i18n";
import { HydratedCanvas, useCanvas, useShareCanvas } from "@zapier/client";
import { Button, SkeletonText } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useCopyToClipboard } from "@zapier/shared";
import { getCanvasPublicUrl, useNotification } from "@zapier/utils";

interface CanvasPublicLinkProps {
  canShare: boolean;
  canvas: HydratedCanvas;
}

export function CanvasPublicLink({ canShare, canvas }: CanvasPublicLinkProps) {
  const t = useTranslations("canvas-public-link");

  const copyToClipboard = useCopyToClipboard();
  const { createNotification } = useNotification();

  const { data: canvasDetails, isLoading: isLoadingShareDetail } = useCanvas(
    canvas?.id,
  );

  const isPublic = canvasDetails?.isPublic ?? false;
  const publicUrl = getCanvasPublicUrl(canvas.id);

  const { isPending: isCreatingShareLink, mutateAsync: createShareLink } =
    useShareCanvas();

  const handleCreateLink = async () => {
    try {
      await createShareLink({ id: canvas.id });
      await copyToClipboard(publicUrl);
    } catch {
      createNotification({
        message: t("errors.create-link-failed"),
        type: "error",
      });
    }
  };

  if (isLoadingShareDetail) {
    return <SkeletonText lines={1} width="20ch" />;
  }

  if (isPublic) {
    return (
      <Button
        onClick={() => copyToClipboard(publicUrl)}
        variant="ghost"
        iconPosition="start"
      >
        <Icon name="actionLink" />
        {t("buttons.copy-view-only-link")}
      </Button>
    );
  }

  return (
    canShare && (
      <Button
        onClick={handleCreateLink}
        variant="ghost"
        iconPosition="start"
        isPending={isCreatingShareLink}
      >
        <Icon name="formAdd" />
        {t("buttons.create-shareable-link")}
      </Button>
    )
  );
}
